import {
    useCallback,
    useContext,
    useEffect,
    useState,
    useMemo
} from "react";
import {
    View
} from "react-native";
import type {
    Organization
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    SelectBox,
    Avatar
} from "ncore-ui-kit";
import type {
    ISelectBoxProps
} from "ncore-ui-kit";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

interface Props {
    spreadBehaviour?: ISelectBoxProps<Record<string, unknown>>["spreadBehaviour"];
}

const RootOrganizationSelector = ({
    spreadBehaviour = "free"
}: Props) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        changeActiveOrganization,
        activeOrganizationId,
        logout
    } = useContext(AuthContext);

    const [
        organizations,
        setOrganizations
    ] = useState<Organization[]>([]);

    const fetchOrganizations = useCallback(async () => {
        try {
            const token = await AsyncStorage.getItem("authToken");
            const response = await fetch(`${process.env.API_URL}/organizations?limit=100`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.status === 401) {
                await logout();
                return;
            }

            const result = await response.json();
            if (result.status === "success") {
                setOrganizations(result.data);
            }
        } catch (error) {
            console.error("Organizasyonlar alınamadı:", error);
        }
    }, [logout]);

    useEffect(() => {
        fetchOrganizations();
    }, [
        fetchOrganizations
    ]);

    const renderPrefix = useCallback(() => {
        if (!activeOrganizationId) {
            return null;
        }

        const activeOrg = organizations.find(org => org.id === activeOrganizationId);

        if (!activeOrg) {
            return null;
        }

        return <Avatar
            imageUrl={activeOrg.profilePicturePath || activeOrg.logo || ""}
            title={activeOrg.name}
            size="xxSmall"
        />;
    }, [
        activeOrganizationId,
        organizations
    ]);

    const mappedData = useMemo(() => {
        return organizations.map(org => ({
            features: org.features || [],
            title: org.name,
            id: org.id
        }));
    }, [
        organizations
    ]);

    const initialSelectedItems = useMemo(() => {
        return mappedData
            .filter(item => item.id === activeOrganizationId)
            .map(item => ({
                ...item,
                __title: item.title,
                __key: item.id
            }));
    }, [
        activeOrganizationId,
        mappedData
    ]);

    return <View
        style={stylesheet.container}
    >
        {
            organizations.length > 0 ?
                <SelectBox
                    onChange={(selectedItems) => {
                        if(!selectedItems || selectedItems.length === 0) {
                            if (activeOrganizationId !== null) {
                                changeActiveOrganization(null);
                            }
                            return;
                        }

                        const item = selectedItems[0] as unknown as typeof mappedData[0] & {
                            __key?: string;
                        };

                        if(!item) {
                            return;
                        }

                        const newId = item.__key || item.id;
                        if (newId === activeOrganizationId) {
                            return;
                        }

                        changeActiveOrganization(newId, item.features);
                    }}
                    placeholder={localize("org-selector-placeholder")}
                    initialSelectedItems={initialSelectedItems}
                    titleExtractor={(item) => item.title}
                    CustomPrefixComponent={renderPrefix}
                    spreadBehaviour={spreadBehaviour}
                    keyExtractor={(item) => item.id}
                    data={mappedData}
                    key="real-box"
                />
            :
                <SelectBox
                    placeholder={localize("org-selector-loading")}
                    spreadBehaviour={spreadBehaviour}
                    titleExtractor={() => ""}
                    initialSelectedItems={[]}
                    keyExtractor={() => ""}
                    key="dummy-box"
                    data={[]}
                />
        }
    </View>;
};
export default RootOrganizationSelector;
